import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Scale, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../lib/dates';
import { TrialBoard } from '../components/crm/TrialBoard';
import { TrialTeamPanel } from '../components/crm/TrialTeamPanel';

const SharedList = () => {
  const { api } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/trial-sim/shared').then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  if (!items.length) return <p data-testid="trial-shared-empty" className="text-sm text-slate-500">Nadie te ha invitado a una simulación todavía.</p>;
  return (
    <div data-testid="trial-shared-list" className="space-y-2">
      {items.map((i) => (
        <Link key={i.id} to={`/simulaciones/${i.id}`} data-testid={`trial-shared-${i.id}`} className="block rounded-xl border border-slate-800 p-4 hover:border-amber-500/60 transition-colors">
          <p className="text-sm font-semibold text-white">{i.title}</p>
          <p className="text-xs text-slate-400">Invitado por {i.owner_name} · {fmtDate(i.created_at)} · {i.stages_done}/{i.stages_total} etapas{i.avg_score != null ? ` · ${i.avg_score}/10` : ''}</p>
        </Link>
      ))}
    </div>
  );
};

// Team simulations: list of sessions shared with me, and the play view for one of them
export default function TrialSimPage() {
  const { sid } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get(`/trial-sim/${sid}`).then((r) => setS(r.data.session)).catch((e) => setErr(e.response?.data?.detail || 'No se pudo abrir')), [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sid) load(); }, [sid, load]);
  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-8 sm:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <button data-testid="trial-page-back" onClick={() => navigate(sid ? '/simulaciones' : '/crm')} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />Volver</button>
        <h1 className="flex items-center gap-3 text-3xl font-bold sm:text-4xl"><Scale className="h-8 w-8 text-amber-400" />{sid ? s?.title || 'Simulación de juicio' : 'Simulaciones en equipo'}</h1>
        {!sid && <><p className="flex items-center gap-2 text-sm text-slate-400"><Users className="h-4 w-4" />Simulaciones de juicio donde otros abogados te sumaron como co-abogado.</p><SharedList /></>}
        {sid && err && <p data-testid="trial-page-error" className="text-sm text-red-400">{err}</p>}
        {sid && !s && !err && <Loader2 className="h-6 w-6 animate-spin text-slate-500" />}
        {s && (
          <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4"><p className="mb-3 text-xs text-slate-400">{s.map?.procedimiento}</p><TrialBoard s={s} setS={setS} /></div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4"><TrialTeamPanel s={s} reload={load} /></div>
          </div>
        )}
      </div>
    </div>
  );
}
